<template>
  <div class="min-h-screen bg-gray-50 px-4 py-6 pb-28" dir="rtl">
    <div class="max-w-2xl mx-auto space-y-6">
      
      <!-- Header Card -->
      <div class="bg-gradient-to-r from-indigo-600 to-indigo-700 rounded-xl p-6 text-white">
        <div class="flex items-center gap-3">
          <div class="w-14 h-12 rounded-full bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center">
            <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          </div>
          <div class="w-full">
            <h2 class="text-xl font-bold">محصولات درخواست</h2>
            <p class="text-sm text-indigo-50">مدیریت و مشاهده محصولات درخواست</p>
          </div>
        </div>
      </div>

      <!-- Add Product Button (only if rec_id == 0) -->
      <button 
        v-if="$route.params.rec_id == 0"
        @click="dialog_model = true"
        class="w-full bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white py-4 px-6 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
      >
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
        </svg>
        {{ $t('addNewProduct') }}
      </button>

      <!-- Empty State -->
      <div v-if="list.length === 0" class="flex flex-col items-center justify-center py-20">
        <div class="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center mb-4">
          <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
          </svg>
        </div>
        <p class="text-gray-500 text-sm">محصولی یافت نشد</p>
      </div>

      <!-- Products List -->
      <div v-else class="space-y-4">
        <div 
          v-for="(item, index) in list" 
          :key="item.Id || index"
          @click="delete_dialog(index)"
          :class="$route.params.rec_id == 0 ? 'cursor-pointer hover:shadow-lg' : ''"
          class="bg-white rounded-xl border border-gray-200 p-5 transition-all duration-200 hover:-translate-y-0.5"
        >
          <!-- Product Header -->
          <div class="mb-4 pb-4 border-b border-gray-200">
            <h3 class="text-md font-bold text-gray-900 mb-2">{{ item.ProductName }}</h3>
            <p class="text-sm text-gray-600">
              <span class="font-medium text-gray-700">{{ item.Qty }}</span>
              <span class="text-gray-500"> {{ $t('numbers') }}</span>
            </p>
          </div>

          <!-- Status Badge -->
          <div class="flex items-center justify-between">
            <div class="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 text-xs font-medium rounded-full">
              {{ item.StatusName }}
            </div>
            <div v-if="$route.params.rec_id == 0" class="text-red-500">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Add Factor Button (only if rec_id == 0 and list has items) -->
      <button 
        v-if="$route.params.rec_id == 0 && list.length"
        @click="add_factor()"
        class="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white py-4 px-6 rounded-xl font-bold transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
      >
        <svg class="w-5 h-5 rotate-180" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
        {{ $t('addNewRequest') }}
      </button>

    </div>

    <!-- Add Product Dialog -->
    <div v-if="dialog_model" class="fixed inset-0 z-999">
      <div class="absolute inset-0 bg-black bg-opacity-60" @click="dialog_model = false"></div>
      <div class="relative z-10 flex h-full w-full items-center justify-center p-4">
        <div class="w-full max-w-md bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-600 to-indigo-700 flex-shrink-0">
            <h2 class="w-full text-center text-lg font-bold text-white">{{ $t('addProduct') }}</h2>
            <button 
              @click="dialog_model = false"
              class="w-8 h-8 rounded-lg bg-white bg-opacity-20 hover:bg-white hover:bg-opacity-30 flex items-center justify-center transition-colors"
            >
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- Body -->
          <div class="px-6 py-6 space-y-4">
            <!-- Product Search -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('searchProduct') }}</label>
              <treeselect
                :multiple="false"
                :load-options="load_models"
                :normalizer="normalizer_models"
                :async="true"
                :placeholder="$t('searchProduct')"
                v-model="auto_complete_model"
                class="treeselect-modern"
              />
            </div>

            <!-- Product Count -->
            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">{{ $t('count') }}</label>
              <input
                v-model="product_count"
                type="number"
                inputmode="numeric"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-all duration-200"
                style="border: 1px solid #d1d5db !important;"
                :placeholder="$t('count')"
              />
            </div>
          </div>

          <!-- Actions -->
          <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              @click="dialog_model = false"
              class="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm font-medium"
            >
              {{ $t('cancel') }}
            </button>
            <button
              @click="goToDetail()"
              class="px-6 py-2 bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-700 hover:to-indigo-800 text-white rounded-lg transition-all duration-200 text-sm font-medium hover:shadow-lg"
            >
              {{ $t('ok') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Dialog -->
    <div v-if="delete_dialog_modal" class="fixed inset-0 z-999">
      <div class="absolute inset-0 bg-black bg-opacity-60" @click="delete_dialog_modal = false"></div>
      <div class="relative z-10 flex h-full w-full items-center justify-center p-4">
        <div class="w-full max-w-sm bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
          <!-- Header -->
          <div class="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-red-600 to-red-700 flex-shrink-0">
            <h2 class="w-full text-center text-lg font-bold text-white">{{ $t('deleteProduct') }}</h2>
            <button 
              @click="delete_dialog_modal = false"
              class="w-8 h-8 rounded-lg bg-white bg-opacity-20 hover:bg-white hover:bg-opacity-30 flex items-center justify-center transition-colors"
            >
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- Body -->
          <div class="px-6 py-6">
            <p class="text-gray-700">{{ $t('sureToDeleteProduct') }}</p>
          </div>

          <!-- Actions -->
          <div class="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
            <button
              @click="delete_dialog_modal = false"
              class="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors text-sm font-medium"
            >
              {{ $t('no') }}
            </button>
            <button
              @click="delete_dialog_modal = false; list.splice(status_form, 1)"
              class="px-6 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg transition-all duration-200 text-sm font-medium hover:shadow-lg"
            >
              {{ $t('yes') }}
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
  import Treeselect, { ASYNC_SEARCH } from '@riophae/vue-treeselect'
  // import the styles
  import '@riophae/vue-treeselect/dist/vue-treeselect.css'

  import axios from 'axios'
  import Vue from 'vue'

  import { bus } from '../../main'

  const simulateAsyncOperation = fn => {
    setTimeout(fn, 200)
  }
  export default {
    props: [
      'passing_data'
    ],
    data() {
      return {
        auto_complete_model: null,
        search_entries: [],
        model_txt: '',

        delete_dialog_modal: false,
        status_form: 0,
        dialog_model: false,
        product_count: '',
        product_items: [],

        branch_id: 0,
        list: [],
        errors: [],
        selected_post: 0,
        selected_branch: 0,
        // request_items: [],
        // last_request_items: [],
        show_loading: false,
        no_info: true,
        page: 0,
        normalizer_models(node) {
          return {
            id: node.ProductId,
            label: node.ProductName,
          }
        },
        first: true,
      }
    },
    computed: {},
    components: { Treeselect },
    watch: {
      auto_complete_model() {
        for (var i = 0; i < this.search_entries.length; i++) {
          if (this.search_entries[i].ProductId == this.auto_complete_model) {
            this.model_txt = this.search_entries[i].ProductName
          }
        }
      },
    },
    mounted() {
      if (this.$route.params.rec_id != 0) {
        this.get_factors()
      }
      this.get_branch_permission()

      this.selected_post = parseInt(localStorage.getItem('selected_post'))
    },

    methods: {
      delete_dialog(index) {
        if (this.$route.params.rec_id == 0) {
          this.delete_dialog_modal = true
          this.status_form = index
        }
      },
      load_models({ action, searchQuery, callback }) {
        this.model_txt = searchQuery
        this.auto_complete_model = null
        var self = this
        if (action === ASYNC_SEARCH) {
          simulateAsyncOperation(() => {
            let options

            var passing_data = JSON.parse(localStorage.getItem('request_data'))

            const send_data = {
              MainProductId: 0,
              TxtSearch: searchQuery,
            }
            this.axios.post('/GetProductLowInfosByKeword', send_data, {})
              .then(response => {
                self.search_entries = response.data.products
                options = response.data.products
                callback(null, options)
                // this.$router.push({ path: "/post-login", params: {} });
              }).catch(e => {
              this.errors.push(e)
            })

            //
            // axios.post('/api/admin/product_search-tags', {
            //   search: searchQuery,
            //
            // }).then(function (response) {
            //   options = response.data;
            //   callback(null, options);
            //   // localStorage.setItem("api_token", response.data.access_token);
            //   // self.$router.push({ path: 'notes' });
            // })
            //   .catch(function (error) {
            //     self.message = 'Incorrect E-mail or password';
            //     self.showMessage = true;
            //     console.log(error);
            //   });

            //  const options = this.get_keywords(searchQuery);
            // const options = [1, 2, 3, 4, 5].map(i => ({
            //     id: `${searchQuery}-${i}`,
            //     name: `${searchQuery}-${i}`,
            // }))
          })
        }
      },

      goToDetail() {
        if (this.auto_complete_model != null && this.product_count != 0 && this.product_count != '') {
          this.list.push({
            ProductId: this.auto_complete_model,
            Qty: this.product_count,
            ProductName: this.model_txt
          })
          this.dialog_model = false
          this.auto_complete_model = null
          this.product_count = ''
        }
      },
      get_factors() {
        // this.request_items = [];
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        const send_data = {
          UserId: user_id,
          MasterId: this.$route.params.rec_id,
        }

        console.log('pre request called')
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/GetGoodsProductsByMasterId', send_data, {})
          .then(response => {
            self.last_request_items = []
            if (response.data.error == 0) {
              self.list = response.data.items
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
      get_branch_permission() {
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId
        var party_id = localStorage.getItem('party_id')

        const send_data = {
          UserId: user_id,
          Role: 'BranchDefinition',
          PartyId: party_id,
        }
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/ZkGetUserPermission', send_data, {})
          .then(response => {
            if (response.data.error == 0) {
              this.branch_id = response.data.UserBranchId
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },

      add_factor() {
        // this.request_items = [];
        var self = this
        var user_id = JSON.parse(localStorage.getItem('profile')).UserId

        const send_data = {
          SourceBranchId: this.branch_id,
          DestBranchId: 0,
          UserId: user_id,
          CostCenterId: this.$route.params.cost_id,
          Products: this.list,
        }

        console.log('pre request called')
        // this.axios.post('/Register' , send_data,{
        this.axios.post('/AddGoodsReq', send_data, {})
          .then(response => {
            self.last_request_items = []
            if (response.data.error == 0) {
              bus.$emit('show_toast', [1500, response.data.msg])
              self.$router.go(-1)
            } else {
              bus.$emit('show_toast', [1500, response.data.msg])
            }
          }).catch(e => {
          this.errors.push(e)
        })
      },
    }
  }
</script>
<style scoped>
  /* Treeselect styling */
  .treeselect-modern :deep(.vue-treeselect__control) {
    border: 1px solid #d1d5db !important;
    border-radius: 0.5rem !important;
    min-height: 48px !important;
    padding: 0 12px !important;
  }
  
  .treeselect-modern :deep(.vue-treeselect__control:hover) {
    border-color: #6366f1 !important;
  }
  
  .treeselect-modern :deep(.vue-treeselect--focused .vue-treeselect__control) {
    border-color: #6366f1 !important;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1) !important;
  }
</style>
